import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  gestionarProductoProveedorHttp,
  getCotizacionPorIdProveedorHttp,
  noDisponibleProductoProveedorHttp,
  onTodosGestionadosHttp,
} from "actions/cotizacion";

export const useCotizacionProveedor = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const IdCotizacion = searchParams.get("id");
  const IdProveedor = searchParams.get("p");
  const intl = useIntl();
  const navigate = useNavigate();

  const idUsuario = useSelector((state: StoreType) => state?.app?.user?.data?.id_usuario || 0);
  const user = useSelector((state: StoreType) => state?.app?.user?.data);

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  const [cotizacion, setCotizacion] = useState<any>(null);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //registrar precio unitario
  const [procesandoPrecio, setProcesandoPrecio] = useState<boolean>(false);
  const [isAlertOpenPrecio, setIsAlertOpenPrecio] = useState<boolean>(false);
  const handleisAlertOpenPrecio = () => setIsAlertOpenPrecio(true);
  const handleisAlertClosePrecio = () => setIsAlertOpenPrecio(false);

  // Handlers para la paginación
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const detallesCotizacionPaginados = useMemo(() => {
    const detalles = cotizacion?.proveedores?.[0]?.detalles ?? [];
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return detalles.slice(startIndex, endIndex);
  }, [cotizacion, page, rowsPerPage]);

  const onTodosGestionados = async (datos: any) => {
    try {
      setProcesando(true);
      await onTodosGestionadosHttp(datos);
      setMensajeAlert(intl.formatMessage({ id: "general_producto_gestionado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_producto_gestionado_error" }));
      handleisAlertOpen();
    }
  };

  const getCotizacionPorId = useCallback(async (data: any) => {
    try {
      setProcesando(true);
      const response = await getCotizacionPorIdProveedorHttp(data);
      setCotizacion(response);
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
      handleisAlertOpen();
    }
  }, []);

  const gestionarProducto = async (datos: any) => {
    try {
      setProcesandoPrecio(true);
      await gestionarProductoProveedorHttp(datos);
      if (IdCotizacion && IdProveedor) {
        await getCotizacionPorId({ id_cotizacion: IdCotizacion, id_proveedor: IdProveedor });
      }
      setMensajeAlert(intl.formatMessage({ id: "general_registrar_precio_correctamente" }));
      handleisAlertClosePrecio();
      handleisAlertOpen();
      setProcesandoPrecio(false);
    } catch (error) {
      setProcesandoPrecio(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_registrar_precio_error" }));
      handleisAlertOpen();
    }
  };
  const noDisponibleProducto = async (datos: any) => {
    try {
      setProcesando(true);
      await noDisponibleProductoProveedorHttp(datos);
      if (IdCotizacion && IdProveedor) {
        await getCotizacionPorId({ id_cotizacion: IdCotizacion, id_proveedor: IdProveedor });
      }
      setMensajeAlert(intl.formatMessage({ id: "general_marcar_no_disponible_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_marcar_no_disponible_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (IdCotizacion && IdProveedor) {
      getCotizacionPorId({ id_cotizacion: IdCotizacion, id_proveedor: IdProveedor });
    }
  }, []);

  return {
    intl,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    cotizacion,
    page,
    rowsPerPage,
    idUsuario,
    detallesCotizacionPaginados,
    handleChangePage,
    handleChangeRowsPerPage,
    gestionarProducto,
    noDisponibleProducto,
    isAlertOpenPrecio,
    handleisAlertOpenPrecio,
    handleisAlertClosePrecio,
    procesandoPrecio,
    onTodosGestionados,
  };
};
